import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CopyableSnippet } from './CopyableSnippet';
import { buildClaudeMcpAddCommand } from '../mcpSetup';
import { postMintPatToken, type MintedPatToken, type PatSummary } from '../api';

/**
 * AOZONE-93: the "Generate MCP token" action. On click it mints a personal
 * access token (POST /api/auth/pat) and reveals the plaintext exactly once, with
 * a copy control and a clear "you won't see this again" warning — the server
 * stores only the hash, so the plaintext is unrecoverable after this.
 *
 * Rendered by UserTokensPage. The endpoint is session-gated server-side and
 * mints only for the caller's own identity (AOZONE-102), which is the real
 * authorization boundary.
 *
 * AOZONE-100/105: `onMinted` fires after a successful mint, carrying the new token's
 * `PatSummary`, so the containing page can PREPEND the row. It deliberately does NOT
 * trigger a re-list: the list endpoint is an eventually-consistent GSI Query, so a
 * refetch here can miss the token that was just minted — and a user who doesn't see
 * their token mints again, ending up with two live 90-day credentials.
 *
 * AOZONE-104: the reveal shows a copy-ready `claude mcp add` with the token
 * already interpolated rather than the bare token — the token is no more exposed
 * than before, and it removes the step most likely to be fumbled. The plaintext
 * is still visible (and selectable) inside the command.
 */
interface GenerateMcpTokenButtonProps {
  onMinted?: (summary: PatSummary) => void;
}

export function GenerateMcpTokenButton({ onMinted }: GenerateMcpTokenButtonProps = {}) {
  const [loading, setLoading] = useState(false);
  const [minted, setMinted] = useState<MintedPatToken | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onClick = async () => {
    setLoading(true);
    setError(null);
    setMinted(null);
    try {
      const result = await postMintPatToken();
      setMinted(result);
      // The guard is LOAD-BEARING, not defensive noise: an older Lambda (or a cached
      // SPA racing a deploy) returns no `summary`, and skipping the prepend degrades
      // that window to "the row appears after a reload" instead of a crash. Do not
      // remove it as dead code — see MintedPatToken.summary.
      if (result.summary) onMinted?.(result.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-3 py-2">
      <Button onClick={onClick} disabled={loading} variant="outline">
        {loading ? 'Generating…' : 'Generate MCP token'}
      </Button>

      {minted && (
        <div
          role="status"
          className="w-full rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <div className="mb-2 font-medium">
            Copy your MCP token now — you won&rsquo;t see it again.
          </div>
          {minted.expiresAt !== undefined && (
            <div className="mb-2 text-xs text-amber-700">
              Expires{' '}
              {new Date(minted.expiresAt * 1000).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
              . On expiry, re-mint a new token here.
            </div>
          )}
          <CopyableSnippet
            text={buildClaudeMcpAddCommand(minted.token)}
            copyLabel="Copy command with your token"
            testId="mcp-add-command-minted"
            tone="amber"
          />
          <div className="mt-2 text-xs text-amber-700">
            Run that to register OZONE with Claude Code. It needs{' '}
            <code className="rounded bg-white px-1 py-0.5 font-mono text-[11px]">/mbr link</code> in
            Slack first — see the setup notes below for that, the JSON form, and how to verify.
          </div>
          {/* The token on its own too: the JSON path (and any non-Claude-Code
              client) needs it without the surrounding command, and this panel is
              the only chance to get it. */}
          <div className="mt-3 text-xs text-amber-700">Or just the token:</div>
          <div className="mt-1">
            <CopyableSnippet
              text={minted.token}
              copyLabel="Copy token"
              testId="minted-token"
              tone="amber"
              wrap
            />
          </div>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="w-full rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          Could not generate a token ({error}).
        </div>
      )}
    </div>
  );
}
